import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./HandselPaywall.module.scss"

/**
 * HandselPaywall (`/handsel`) — a self-contained ONBOARDING paywall for Private Photo
 * Vault built as a WELCOME GIFT. A "handsel" is a good-luck gift given at the start of
 * something new; here the app hands the brand-new user a wrapped keepsake box on day
 * one. You PULL THE RIBBON — the satin bow unspools and slips off, the lid hinges open,
 * and the box gives up what's inside: your first memories nested in tissue, each
 * getting a small gold "kept" seal, and the four real Premium capabilities fan out on
 * little gift tags. The gift IS your first year, which is exactly the plan we feature.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/handsel.md
 *
 * NEW mechanic (not in the ledger): PULL-THE-RIBBON / UNTIE-THE-BOW. A progressive
 * ribbon-pull that unspools a bow and slides the ribbon off a box, then the lid lifts.
 * Not a tear, crank, haul, lever-pull, pour, scrub, dial, wind, fold, press-seal,
 * riffle or slider — and NOT a grey→colour reveal or a light-flood (the photos are full
 * colour throughout; opening changes only whether the gift is UNWRAPPED).
 *
 * NEW layout: a ribboned GIFT BOX that opens into a "what's inside" tray — no centred
 * glowing card, no object-on-a-pedestal stage, no before/after split, no grid-only.
 *
 * Value frame: RECIPROCITY (rare — 1 prior use, never for onboarding) with a touch of
 * variable-reward curiosity ("what's in the box"). The honest engine for YEARLY: the
 * real 7-day free trial is framed as the welcome gift, and the box is literally "your
 * first year," so keeping the year is the natural next beat. Register: delighted,
 * generous, warm-surprise — deliberately unlike the cosmic-awe / deep-calm / precious-
 * permanence registers the other onboarding paywalls already occupy.
 *
 * Honest & App Store compliant: yearly is the featured plan and carries the most
 * prominent price ($/yr) plus its exact trial terms; monthly is a small secondary line.
 * The savings figure is exact arithmetic off the real debug products (1 − yearly ÷
 * monthly×12). No countdown, no fake scarcity, no misleading savings claim. The "gift"
 * is the genuine free trial + a warm welcome, never a claim that the product is free.
 * Copy never implies photos are lost when a plan ends — it says the opposite. Features
 * are real Premium features. Photos are bundled repo images (no network egress).
 */

type PlanKey = "yearly" | "monthly"

// the memories nested in the box — bundled everyday photos already in the repo
const GIFTS = [
  { src: "/recall/sunset-1.jpg", pos: "50% 45%" },
  { src: "/recall/dog-2.jpg", pos: "50% 40%" },
  { src: "/recall/beach-2.jpg", pos: "50% 55%" },
  { src: "/recall/coffee-1.jpg", pos: "50% 50%" },
  { src: "/recall/flower-1.jpg", pos: "50% 45%" },
  { src: "/recall/mountain-1.jpg", pos: "50% 50%" },
  { src: "/recall/city-1.jpg", pos: "50% 50%" },
]

// real Premium capabilities — presented as gift tags tucked in the box
const INSIDE = [
  {
    icon: "cloud",
    title: "Backed up the moment you add them",
    note: "Automatic private cloud backup",
  },
  {
    icon: "lock",
    title: "Locked away, only you can open it",
    note: "Private & encrypted",
  },
  {
    icon: "hd",
    title: "Every photo & video, full quality",
    note: "Original resolution, untouched",
  },
  {
    icon: "devices",
    title: "Your vault, everywhere you sign in",
    note: "On all your devices",
  },
]

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

const Icon = ({ name, size = 16 }: { name: string; size?: number }) => {
  const c = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      {name === "lock" && (
        <>
          <rect x="5" y="10.5" width="14" height="9.5" rx="2.2" {...c} />
          <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" {...c} />
        </>
      )}
      {name === "hd" && (
        <>
          <rect x="3.5" y="5" width="17" height="14" rx="2.4" {...c} />
          <path d="M7 9.5v5M7 12h2.4M9.4 9.5v5" {...c} />
          <path
            d="M12.8 9.5v5h1.8a2 2 0 0 0 2-2v-1a2 2 0 0 0-2-2h-1.8Z"
            {...c}
          />
        </>
      )}
      {name === "cloud" && (
        <path
          d="M7 18a4 4 0 0 1-.4-8 5.5 5.5 0 0 1 10.6 1.2A3.4 3.4 0 0 1 17 18H7Z"
          {...c}
        />
      )}
      {name === "devices" && (
        <>
          <rect x="3" y="6" width="12" height="9" rx="1.6" {...c} />
          <rect x="16" y="9" width="5" height="9" rx="1.4" {...c} />
          <path d="M6 18h6" {...c} />
        </>
      )}
      {name === "shield" && (
        <path
          d="M12 2.6l7 2.7v5.2c0 4.6-3 8.2-7 10.9-4-2.7-7-6.3-7-10.9V5.3l7-2.7Z"
          {...c}
        />
      )}
      {name === "check" && <path d="M5 12.5l4.2 4.2L19 7" {...c} />}
      {name === "arrow" && (
        <>
          <path d="M5 12h13" {...c} />
          <path d="M12.5 6l6 6-6 6" {...c} />
        </>
      )}
      {name === "spark" && (
        <path d="M12 3v6M12 15v6M3 12h6M15 12h6" {...c} strokeWidth={2.1} />
      )}
    </svg>
  )
}

const HandselPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (real debug data; yearly featured) ──────────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPriceNum = yearlyProduct?.price ?? 35.99
  const monthlyPriceNum = monthlyProduct?.price ?? 13.99

  const yearlyPrice = formatCurrency(yearlyPriceNum, locale)
  const monthlyPrice = formatCurrency(monthlyPriceNum, locale)
  const yearlyPerMonth = formatCurrency(yearlyPriceNum / 12, locale)

  const yearlyTrial = yearlyProduct?.hasTrial
    ? (yearlyProduct?.introPricing?.period ?? 7)
    : 0
  const monthlyTrial = monthlyProduct?.hasTrial
    ? (monthlyProduct?.introPricing?.period ?? 3)
    : 0

  const savingsPct = useMemo(() => {
    if (!monthlyPriceNum) return 0
    return Math.max(
      0,
      Math.round((1 - yearlyPriceNum / (monthlyPriceNum * 12)) * 100),
    )
  }, [yearlyPriceNum, monthlyPriceNum])

  // ── unwrap state ────────────────────────────────────────────────────────────
  const [pull, setPull] = useState(0) // 0..1 how far the ribbon has been pulled
  const [opened, setOpened] = useState(false) // gift fully unwrapped
  const [engaged, setEngaged] = useState(false)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)

  const tabWrapRef = useRef<HTMLDivElement | null>(null)
  const pullRef = useRef(0)
  const dragRef = useRef<{ x: number; y: number; pull: number } | null>(null)
  const rafRef = useRef<number | null>(null)

  const open = useCallback(() => {
    setOpened((was) => {
      if (!was) native.logMixpanelEvent("html_handsel_opened", {})
      return true
    })
  }, [])

  const setPullClamped = useCallback(
    (v: number) => {
      const nv = clamp(v, 0, 1)
      pullRef.current = nv
      setPull(nv)
      if (nv >= 1) open()
    },
    [open],
  )

  // reduced motion — arrive already unwrapped, no animation
  useEffect(() => {
    if (!reduceMotion) return
    pullRef.current = 1
    setPull(1)
    setOpened(true)
    setEngaged(true)
    setShowClose(true)
  }, [reduceMotion])

  // reveal close after a beat (not tied to opening the gift)
  useEffect(() => {
    if (reduceMotion) return
    const t = window.setTimeout(() => setShowClose(true), 1400)
    return () => window.clearTimeout(t)
  }, [reduceMotion])

  // warm the safe-area strips to the paper
  useEffect(() => {
    actions.setHeaderInsetColor("#fbf4ea")
    actions.setFooterInsetColor("#ffffff")
  }, [actions])

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    },
    [],
  )

  // ── input: pull the ribbon ──────────────────────────────────────────────────
  const onPointerDown = (e: React.PointerEvent) => {
    if (opened) return
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    dragRef.current = { x: e.clientX, y: e.clientY, pull: pullRef.current }
    if (!engaged) {
      setEngaged(true)
      native.logMixpanelEvent("html_handsel_grab", {})
    }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const base = dragRef.current
    if (!base || opened) return
    // pull the tab down-and-out; distance along the diagonal drives the unspool
    const dx = e.clientX - base.x
    const dy = e.clientY - base.y
    const dist = Math.max(dy, (dy + Math.abs(dx)) * 0.6)
    setPullClamped(base.pull + dist / 150)
  }
  const onPointerUp = () => {
    const base = dragRef.current
    dragRef.current = null
    if (!base || opened) return
    // past the point of no return → finish untying; otherwise spring back
    if (pullRef.current >= 0.55) finishUnwrap(pullRef.current)
    else springBack(pullRef.current)
  }

  const springBack = (from: number) => {
    if (reduceMotion) return setPullClamped(0)
    const t0 = performance.now()
    const step = (now: number) => {
      const k = clamp((now - t0) / 240, 0, 1)
      setPullClamped(from * (1 - k))
      if (k < 1) rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
  }

  const finishUnwrap = (from: number) => {
    if (reduceMotion) return setPullClamped(1)
    const t0 = performance.now()
    const step = (now: number) => {
      const k = clamp((now - t0) / 360, 0, 1)
      const e = 1 - Math.pow(1 - k, 3) // ease-out cubic
      setPullClamped(from + (1 - from) * e)
      if (k < 1) rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
  }

  const openViaButton = () => {
    if (opened) return
    setEngaged(true)
    native.logMixpanelEvent("html_handsel_open_button", {})
    finishUnwrap(pullRef.current)
  }

  const onTabKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      openViaButton()
    }
  }

  // ── plan + purchase wiring ──────────────────────────────────────────────────
  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    if (!opened) openViaButton()
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    native.logMixpanelEvent("html_handsel_purchase", { plan })
    if (product) native.subscribe(product)
  }

  const trialDays = plan === "yearly" ? yearlyTrial : monthlyTrial
  const ctaSub =
    plan === "yearly"
      ? trialDays > 0
        ? `${trialDays} days free, then ${yearlyPrice}/year · cancel anytime`
        : `${yearlyPrice}/year · cancel anytime`
      : trialDays > 0
        ? `${trialDays} days free, then ${monthlyPrice}/month · cancel anytime`
        : `${monthlyPrice}/month · cancel anytime`

  const hint = opened
    ? "It's all yours"
    : engaged
      ? pull > 0.2
        ? "Keep pulling…"
        : "Pull the ribbon down"
      : "Pull the ribbon to open your gift"

  // stage css vars drive the whole unwrap in scss
  const stageVars = {
    ["--pull" as any]: pull,
    ["--open" as any]: opened ? 1 : 0,
  }

  return (
    <div className={`${styles.page} ${opened ? styles.pageOpen : ""}`}>
      <button
        className={`${styles.close} ${showClose ? styles.closeShown : ""}`}
        onClick={handleClose}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <span className={styles.brand}>
          <span className={styles.brandMark} aria-hidden>
            <Icon name="shield" size={13} />
          </span>
          Private Photo Vault
        </span>
        <h1 className={styles.title}>
          Welcome. Here&apos;s a little gift
          <br />
          to start your vault.
        </h1>
        <p className={styles.sub}>
          We wrapped up your first year — every memory private, backed up and in
          full quality. Pull the ribbon and see what&apos;s inside.
        </p>
      </header>

      {/* ── the gift ─────────────────────────────────────────────────────────── */}
      <section className={styles.stage} style={stageVars}>
        <div className={`${styles.gift} ${opened ? styles.giftOpen : ""}`}>
          {/* soft glow that warms up as the box opens */}
          <span className={styles.halo} aria-hidden />

          {/* what's nested inside — rises as the lid lifts */}
          <div className={styles.contents} aria-hidden={!opened}>
            <span className={styles.tissue} aria-hidden />
            <div className={styles.photoFan}>
              {GIFTS.map((g, i) => (
                <span
                  key={g.src}
                  className={styles.photo}
                  style={{
                    backgroundImage: `url(${g.src})`,
                    backgroundPosition: g.pos,
                    ["--pi" as any]: i,
                    ["--pn" as any]: GIFTS.length,
                  }}
                >
                  <span className={styles.photoSeal} aria-hidden>
                    <Icon name="check" size={12} />
                  </span>
                </span>
              ))}
            </div>
            <span className={styles.tag} aria-hidden>
              your first year
            </span>
          </div>

          {/* the box body */}
          <div className={styles.box}>
            <span className={styles.boxFront} aria-hidden />
            <span className={styles.ribbonDown} aria-hidden />
          </div>

          {/* the lid that hinges open */}
          <div className={styles.lid} aria-hidden>
            <span className={styles.lidRibbon} />
            <span className={styles.giftCard}>
              <span className={styles.giftCardTo}>To you</span>
              <span className={styles.giftCardFrom}>from your vault</span>
            </span>
          </div>

          {/* the bow + pull-tab — the interactive part */}
          <div
            className={styles.bowWrap}
            ref={tabWrapRef}
            role="button"
            tabIndex={0}
            aria-label="Pull the ribbon to open your gift"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={onTabKey}
          >
            <svg
              className={styles.bow}
              viewBox="0 0 120 90"
              width="120"
              height="90"
              aria-hidden
            >
              <path
                className={styles.bowLoop}
                d="M60 46 C 40 20, 8 24, 14 46 C 8 68, 44 70, 60 46 Z"
              />
              <path
                className={styles.bowLoop}
                d="M60 46 C 80 20, 112 24, 106 46 C 112 68, 76 70, 60 46 Z"
              />
              <circle className={styles.bowKnot} cx="60" cy="46" r="10" />
            </svg>
            <span className={styles.tail} aria-hidden />
            <span className={styles.tab} aria-hidden>
              <Icon name="arrow" size={16} />
            </span>
          </div>

          {/* one-shot delight burst on open */}
          {opened && !reduceMotion && (
            <span className={styles.burst} aria-hidden>
              {Array.from({ length: 14 }).map((_, i) => (
                <i key={i} style={{ ["--bi" as any]: i }} />
              ))}
            </span>
          )}
        </div>

        <div className={styles.stageFoot}>
          {opened ? (
            <span className={styles.doneLine}>
              <Icon name="shield" size={14} />A year of memories — private,
              backed up, full quality
            </span>
          ) : (
            <>
              <span className={styles.hint}>{hint}</span>
              <button className={styles.openBtn} onClick={openViaButton}>
                Open my gift
              </button>
            </>
          )}
        </div>
      </section>

      {/* what was inside — real Premium capabilities as gift tags (appear on open) */}
      {opened && (
        <ul className={styles.inside}>
          {INSIDE.map((f, i) => (
            <li
              key={f.title}
              className={styles.tagCard}
              style={{ ["--ti" as any]: i }}
            >
              <span className={styles.tagIcon} aria-hidden>
                <Icon name={f.icon} size={17} />
              </span>
              <span className={styles.tagText}>
                <b>{f.title}</b>
                <i>{f.note}</i>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* plans — yearly featured (it IS the gift); monthly small & secondary */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.sel : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.ribbonBadge} aria-hidden>
            <Icon name="spark" size={12} />
            YOUR GIFT
          </span>
          <span className={styles.planMain}>
            <span className={styles.radio} aria-hidden />
            <span className={styles.planNameWrap}>
              <span className={styles.planName}>Keep your first year</span>
              <span className={styles.planNote}>
                {yearlyTrial > 0
                  ? `${yearlyTrial} days free, then ${yearlyPrice}/yr`
                  : `${yearlyPrice} billed yearly`}
                {" · "}
                {yearlyPerMonth}/mo
              </span>
            </span>
          </span>
          <span className={styles.planPrice}>
            {savingsPct > 0 && (
              <span className={styles.savePill}>SAVE {savingsPct}%</span>
            )}
            <span className={styles.planBig}>
              {yearlyPrice}
              <span className={styles.planPer}>/yr</span>
            </span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.selMonth : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Or start monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>
          {plan === "yearly" ? "Keep my first year" : "Start monthly"}
        </span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Your photos are always yours — nothing is ever deleted if a plan ends.
        Premium simply keeps a private, backed-up copy of every memory safe.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default HandselPaywall
